import {
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  FieldError,
  Label,
  TextArea as AriaTextArea,
  Text,
} from "react-aria-components";
import { cn } from "@core/utils";

interface TextAreaProps extends Omit<
  AriaTextFieldProps,
  "className" | "children"
> {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  placeholder?: string;
  /** Helper copy under the field (design: sm/Regular, text-tertiary). */
  description?: string;
  /** Error text; presence also marks the field invalid. */
  errorMessage?: string;
  /** Visible rows at 320px; the control still grows with `resize-y`. */
  rows?: number;
}

/**
 * Multi-line text primitive — the `TextField` box treatment applied to React Aria's
 * `TextArea`, so a textarea and an input read as the same control family. Used by the round
 * builder's description field.
 */
export function TextArea({
  label,
  hideLabel = false,
  placeholder,
  description,
  errorMessage,
  rows = 4,
  ...props
}: TextAreaProps) {
  const invalid = Boolean(errorMessage) || props.isInvalid;

  return (
    <AriaTextField
      isInvalid={invalid}
      className="flex w-full flex-col gap-1.5"
      {...props}
    >
      <Label
        className={cn(
          "text-sm-medium text-(--text-secondary)",
          hideLabel && "sr-only"
        )}
      >
        {label}
        {props.isRequired ? (
          <span aria-hidden className="text-(--text-error)">
            {" "}
            *
          </span>
        ) : null}
      </Label>

      <AriaTextArea
        rows={rows}
        placeholder={placeholder}
        className={cn(
          "w-full resize-y rounded-md bg-surface-field px-3.5 py-2.5 shadow-xs",
          "border text-md-regular text-(--text-primary) outline-none transition-colors",
          "placeholder:text-(--text-placeholder)",
          "focus:ring-2 focus:ring-focus-ring",
          "disabled:bg-surface-field-disabled disabled:text-(--text-disabled)",
          invalid
            ? "border-(--border-field-error) focus:ring-error-500"
            : "border-(--border-field) focus:border-(--focus-ring)"
        )}
      />

      {description ? (
        <Text
          slot="description"
          className="text-sm-regular text-(--text-tertiary)"
        >
          {description}
        </Text>
      ) : null}

      <FieldError className="text-sm-regular text-(--text-error)">
        {errorMessage}
      </FieldError>
    </AriaTextField>
  );
}

export type { TextAreaProps };
